import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Clock, ArrowLeft, Globe, Users, Lock, CreditCard
} from 'lucide-react';
import { useContactForm } from '../context/ContactFormContext';

interface Course {
  id: number;
  name: string;
  price: number;
  level: string;
  duration: string;
  practice: string;
  description: string;
  fullDescription: string;
  features: string[];
}

const CourseDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { openBuy } = useContactForm();
  
  const coursesData = t('courses.items', { returnObjects: true }) as Course[];
  const course = coursesData.find(c => c.id === Number(id));

  const levelColors: Record<string, string> = {
    beginner: 'from-green-500 to-emerald-500',
    intermediate: 'from-blue-500 to-cyan-500',
    advanced: 'from-purple-500 to-violet-500',
    professional: 'from-orange-500 to-amber-500',
    elite: 'from-pink-500 to-rose-500',
    master: 'from-red-500 to-rose-600',
    guru: 'from-crypto-red to-red-400',
    legend: 'from-crypto-red via-red-500 to-red-600'
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-crypto-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">{t('courseDetail.notFound')}</h1>
          <Link to="/" className="text-crypto-red hover:underline">
            {t('courseDetail.backHome')}
          </Link>
        </div>
      </div>
    );
  }

  const gradientClass = levelColors[course.level] || 'from-gray-500 to-gray-400';

  return (
    <div className="min-h-screen bg-crypto-black">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 bg-crypto-black/90 backdrop-blur-xl border-b border-crypto-red/20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-crypto-red to-crypto-gradient-end flex items-center justify-center">
                <span className="text-white font-bold text-xl">R</span>
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-bold text-white">RedLevel</span>
                <span className="text-crypto-red font-bold text-xl">Crypto</span>
              </div>
            </Link>
            
            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/#courses" className="text-gray-300 hover:text-crypto-red transition-colors text-sm font-medium">
                {t('nav.courses')}
              </Link>
              <Link to="/#about" className="text-gray-300 hover:text-crypto-red transition-colors text-sm font-medium">
                {t('nav.about')}
              </Link>
              <Link to="/#faq" className="text-gray-300 hover:text-crypto-red transition-colors text-sm font-medium">
                {t('nav.faq')}
              </Link>
              <Link to="/#footer" className="text-gray-300 hover:text-crypto-red transition-colors text-sm font-medium">
                {t('nav.contact')}
              </Link>
            </nav>
            
            <Link 
              to="/#courses" 
              className="flex items-center gap-2 text-gray-400 hover:text-crypto-red transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t('courseDetail.allCourses')}</span>
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${gradientClass} opacity-10`} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-crypto-red/5 rounded-full blur-[150px]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            {/* Left - Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-crypto-card rounded-full border border-crypto-red/20">
                  <span className="text-sm text-gray-300">{t(`courses.levels.${course.level}`)}</span>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {course.name}
              </h1>
              
              <p className="text-xl text-gray-400 mb-8">
                {course.description}
              </p>

              <div className="flex items-baseline gap-4 mb-8">
                <span className="text-5xl font-bold text-crypto-red">${course.price}</span>
              </div>

              <div className="flex flex-wrap gap-4">
                <motion.button
                  onClick={() => openBuy(course.name)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-8 py-4 bg-gradient-to-r ${gradientClass} text-white font-bold rounded-xl text-lg hover:shadow-xl`}
                >
                  {t('courses.buy')}
                </motion.button>
                
                <div className="flex items-center gap-2 px-6 py-4 bg-crypto-card border border-crypto-red/20 rounded-xl">
                  <CreditCard className="w-5 h-5 text-crypto-red" />
                  <span className="text-gray-400 text-sm">{t('courseDetail.paymentBadge')}</span>
                </div>
              </div>
            </div>

            {/* Right - Stats */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-crypto-card border border-crypto-red/10 rounded-2xl p-8"
            >
              <h3 className="text-xl font-bold text-white mb-6">{t('courseDetail.specs')}</h3>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-crypto-red/10 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-crypto-red" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('courses.duration')}</div>
                    <div className="font-semibold text-white">{course.duration}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-crypto-red/10 rounded-xl flex items-center justify-center">
                    <span className="text-crypto-red font-bold">P</span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('courses.practice')}</div>
                    <div className="font-semibold text-white">{course.practice}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Full Description */}
      <section className="py-16 bg-crypto-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-3 gap-12"
          >
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-white mb-6">{t('courseDetail.description')}</h2>
              <div className="prose prose-invert max-w-none">
                <div className="text-gray-300 leading-relaxed whitespace-pre-line text-lg">
                  {course.fullDescription}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Includes */}
              <div className="bg-crypto-card border border-crypto-red/10 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">{t('courseDetail.includes')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-crypto-red" />
                    <span className="text-gray-300">{t('courses.community')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-crypto-red" />
                    <span className="text-gray-300">{t('courses.lifetime')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-crypto-red" />
                    <span className="text-gray-300">{t('courses.mentorship')}</span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="bg-crypto-card border border-crypto-red/10 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">{t('courseDetail.features')}</h3>
                <div className="space-y-3">
                  {course.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-crypto-red">•</span>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-crypto-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              {t('courseDetail.readyTitle')}
            </h2>
            <p className="text-gray-400 mb-8">
              {t('courseDetail.readySubtitle', { name: course.name })}
            </p>
            <motion.button
              onClick={() => openBuy(course.name)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-10 py-4 bg-gradient-to-r ${gradientClass} text-white font-bold rounded-xl text-lg hover:shadow-xl`}
            >
              {t('courseDetail.buyFor', { price: course.price })}
            </motion.button>
            <p className="mt-4 text-sm text-gray-500">
              {t('courseDetail.paymentNote')}
            </p>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default CourseDetail;
